import { createMiddleware, createServerOnlyFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import type {
  IRateLimiterOptions,
  RateLimiterRes,
} from "rate-limiter-flexible";

import { getViewer } from "@/auth/server";
import { db } from "@/db/connection";
import { rateLimiterFlexibleSchema } from "@/db/schema";

import { RateLimiterDrizzle } from "./rate-limiter-drizzle";
import { internalServerError, tooManyRequests } from "./responses";

type RateLimitConfig = Pick<
  IRateLimiterOptions,
  "keyPrefix" | "points" | "duration" | "blockDuration"
>;

type RateLimitKeyConfig = RateLimitConfig & { key: string };

async function consumeOrThrow(rateLimiter: RateLimiterDrizzle, key: string) {
  try {
    return rateLimiter.consume(key, 1);
  } catch (rejection) {
    if (rejection instanceof Error) {
      throw internalServerError(rejection.message);
    }

    const result = rejection as RateLimiterRes;
    const retrySecs = Math.round(result.msBeforeNext / 1000) || 1;

    throw tooManyRequests(retrySecs);
  }
}

export const consumeRateLimit = createServerOnlyFn(
  async ({
    keyPrefix,
    points,
    duration,
    blockDuration,
    key,
  }: RateLimitKeyConfig) => {
    const rateLimiter = new RateLimiterDrizzle({
      storeClient: db,
      schema: rateLimiterFlexibleSchema,
      keyPrefix,
      points,
      duration,
      blockDuration,
    });

    await consumeOrThrow(rateLimiter, key);
  },
);

export const rateLimitMiddleware = ({
  keyPrefix,
  points,
  duration,
  blockDuration,
}: RateLimitConfig) =>
  createMiddleware().server(async ({ next }) => {
    const rateLimiter = new RateLimiterDrizzle({
      storeClient: db,
      schema: rateLimiterFlexibleSchema,
      keyPrefix,
      points,
      duration,
      blockDuration,
    });

    const viewer = await getViewer();

    await consumeOrThrow(rateLimiter, viewer.id);

    return next();
  });

export const ipRateLimitMiddleware = ({
  keyPrefix,
  points,
  duration,
  blockDuration,
}: RateLimitConfig) =>
  createMiddleware().server(async ({ next }) => {
    const rateLimiter = new RateLimiterDrizzle({
      storeClient: db,
      schema: rateLimiterFlexibleSchema,
      keyPrefix,
      points,
      duration,
      blockDuration,
    });

    const request = getRequest();
    const ip =
      request.headers.get("x-forwarded-for")?.split(",")[0]?.trim() ??
      "unknown";

    await consumeOrThrow(rateLimiter, ip);

    return next();
  });
